<template>
  <div class="report-assistant-dialog">
    <!-- Subtitle -->
    <p class="assistant-subtitle">Type naturally to filter, sort, and customize your report</p>
    
    <!-- Guidance Section -->
    <div class="guidance-section">
      <div class="guidance-content">
        <i class="fa fa-lightbulb-o"></i>
        <span>Include action keywords in your command to help AI understand your intent</span>
      </div>
    </div>
    
    <!-- Command Input -->
    <div class="command-input-section">
      <div class="input-wrapper">
        <input
          ref="commandInput"
          v-model="commandText"
          @keydown.enter="executeCommand"
          @keydown.up="navigateHistory(-1)"
          @keydown.down="navigateHistory(1)"
          @input="onCommandInput"
          :disabled="processing"
          type="text"
          class="command-input"
          :placeholder="currentPlaceholder"
          autocomplete="off"
        />
        <button
          @click="executeCommand"
          :disabled="!commandText.trim() || processing"
          :class="['execute-button', { 'processing': processing }]"
        >
          <template v-if="!processing">
            <i class="fa fa-play"></i>
            <span>Run</span>
          </template>
          <template v-else>
            <div class="spinner-container">
              <div class="ai-spinner"></div>
            </div>
            <span>{{ processingButtonText }}</span>
          </template>
        </button>
      </div>
      
      <!-- AI Thinking Indicator -->
      <transition name="fade">
        <div v-if="processing" class="ai-thinking-indicator">
          <div class="thinking-animation">
            <div class="thinking-dot"></div>
            <div class="thinking-dot"></div>
            <div class="thinking-dot"></div>
            <div class="thinking-dot"></div>
          </div>
          <p class="thinking-message">{{ processingMessages[currentProcessingMessage] }}</p>
          <div class="progress-bar">
            <div class="progress-fill"></div>
          </div>
        </div>
      </transition>
      
      <!-- Auto Apply Option -->
      <div class="auto-apply-wrapper">
        <label class="auto-apply-label">
          <input 
            type="checkbox" 
            v-model="autoApply"
            class="auto-apply-checkbox"
          />
          <span>Auto-apply changes</span>
          <i 
            class="fa fa-info-circle"
            title="When enabled, detected changes will be applied automatically without showing the review sections"
          ></i>
        </label>
      </div>

      <!-- Command Type Chips -->
      <div class="command-suggestions">
        <div class="keyword-header">
          <span class="keyword-title">Action Keywords Reference</span>
          <span class="keyword-subtitle">Click to see examples • Use these words in your commands</span>
        </div>
        <div class="command-chips">
          <div 
            v-for="cmd in commandTypes" 
            :key="cmd.id"
            @click="selectAction(cmd.id)"
            :class="['command-chip', 'keyword-chip', { active: selectedAction === cmd.id }]"
            :style="{ 
              borderColor: selectedAction === cmd.id ? cmd.color : '#e0e0e0',
              backgroundColor: selectedAction === cmd.id ? cmd.color + '10' : 'white'
            }"
            :title="`Click to see examples and insert '${cmd.label.toLowerCase()}' keyword`"
          >
            <i :class="cmd.icon" :style="{ color: cmd.color }"></i>
            <span>{{ cmd.label }}</span>
          </div>
        </div>
        
        <!-- Action-based Examples -->
        <transition name="fade">
          <div v-if="selectedAction" class="examples-container compact">
            <div class="examples-header">
              <span>Examples for {{ getActionLabel(selectedAction) }}:</span>
              <button @click="selectedAction = null" class="close-examples">
                <i class="fa fa-times"></i>
              </button>
            </div>
            <div class="examples-grid">
              <div 
                v-for="(example, idx) in currentActionExamples" 
                :key="idx"
                @click="setCommand(example)"
                class="example-chip"
                :title="`Click to use: ${example}`"
              >
                <span>{{ example }}</span>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Command Results Section -->
    <div v-if="showFilterSection || showSortSection || showColumnsSection || showTimeRangeSection" class="results-header">
      <i class="fa fa-magic"></i>
      <span>Command Results - Review and Apply</span>
    </div>

    <!-- Filters Section -->
    <div v-if="showFilterSection && hasActiveFilters" class="action-section">
      <div class="section-header">
        <div class="header-left">
          <i class="fa fa-filter"></i>
          <span class="section-title">Filters ({{ activeFilters.length }})</span>
        </div>
        <div class="header-actions">
          <button @click="applyFilters" class="apply-btn">
            <i class="fa fa-check"></i>
            Apply Filters
          </button>
        </div>
      </div>
      <div class="filter-list">
        <div v-for="(filter, idx) in activeFilters" :key="idx" class="filter-item">
          <span class="filter-name">{{ formatFilterName(filter.name) }}</span>
          <span class="filter-operator">{{ getOperatorLabel(filter.operator) }}</span>
          <span class="filter-value">{{ formatValue(filter.value) }}</span>
          <button @click="removeFilter(idx)" class="remove-button" title="Remove filter">
            ×
          </button>
        </div>
        <div v-if="filterLogic && activeFilters.length > 1" class="filter-logic">
          Logic: <span class="logic-value">{{ filterLogic }}</span>
        </div>
      </div>
    </div>

    <!-- Sort Section -->
    <div v-if="showSortSection && currentSortState" class="action-section">
      <div class="section-header">
        <div class="header-left">
          <i class="fa fa-sort"></i>
          <span class="section-title">Sort</span>
        </div>
        <div class="header-actions">
          <button @click="applySort" class="apply-btn">
            <i class="fa fa-check"></i>
            Apply Sort
          </button>
        </div>
      </div>
      <div class="sort-item">
        <span>{{ currentSortState.columnName || currentSortState.column }} ({{ currentSortState.direction === 'asc' ? 'ascending' : 'descending' }})</span>
        <i :class="currentSortState.direction === 'asc' ? 'fa fa-arrow-up' : 'fa fa-arrow-down'"></i>
        <button @click="clearSort" class="remove-button" title="Clear sort">
          ×
        </button>
      </div>
    </div>

    <!-- Columns Section -->
    <div v-if="showColumnsSection && activeColumns && activeColumns.length > 0" class="action-section">
      <div class="section-header">
        <div class="header-left">
          <i class="fa fa-columns"></i>
          <span class="section-title">Columns to Add ({{ activeColumns.length }})</span>
        </div>
        <div class="header-actions">
          <button v-if="hasColumnChanges" @click="applyColumns" class="apply-btn">
            <i class="fa fa-check"></i>
            Apply Columns
          </button>
        </div>
      </div>
      <div class="columns-list">
        <div v-for="(column, index) in activeColumns" :key="index" class="column-item">
          <i class="fa fa-table"></i>
          <span class="column-name">{{ column.name || column.code }}</span>
          <span class="column-type" v-if="column.dataType">({{ column.dataType }})</span>
        </div>
      </div>
    </div>

    <!-- Limit Section -->
    <div v-if="showLimitSection && currentLimitState" class="action-section">
      <div class="section-header">
        <div class="header-left">
          <i class="fa fa-list-ol"></i>
          <span class="section-title">Limit</span>
        </div>
        <div class="header-actions">
          <button @click="applyLimit" class="apply-btn">
            <i class="fa fa-check"></i>
            Apply Limit
          </button>
        </div>
      </div>
      <div class="limit-item">
        <span>Show {{ currentLimitState.limit }} items</span>
        <button @click="clearLimit" class="remove-button" title="Clear limit">
          ×
        </button>
      </div>
    </div>

    <!-- Time Range Section -->
    <div v-if="showTimeRangeSection && currentTimeRangeState" class="action-section">
      <div class="section-header">
        <div class="header-left">
          <i class="fa fa-calendar"></i>
          <span class="section-title">Time Range</span>
        </div>
        <div class="header-actions">
          <button @click="applyTimeRange" class="apply-btn">
            <i class="fa fa-check"></i>
            Apply Time Range
          </button>
        </div>
      </div>
      <div class="time-range-item">
        <span>{{ currentTimeRangeState.description || formatTimeRange(currentTimeRangeState.time_range) }}</span>
        <span v-if="currentTimeRangeState.time_range" class="date-range">
          ({{ currentTimeRangeState.time_range.from }} to {{ currentTimeRangeState.time_range.to }})
        </span>
        <button @click="clearTimeRange" class="remove-button" title="Clear time range">
          ×
        </button>
      </div>
    </div>

    <!-- Group Section (Dimension Switching) -->
    <div v-if="showGroupSection && currentGroupState" class="action-section">
      <div class="section-header">
        <div class="header-left">
          <i class="fa fa-object-group"></i>
          <span class="section-title">Change Dimension</span>
        </div>
        <div class="header-actions">
          <button @click="applyGroup" class="apply-btn">
            <i class="fa fa-check"></i>
            Apply Dimension
          </button>
        </div>
      </div>
      <div class="group-item">
        <span>Switch to: {{ currentGroupState.dimension_name }} ({{ currentGroupState.dimension }})</span>
        <button @click="clearGroup" class="remove-button" title="Cancel dimension change">
          ×
        </button>
      </div>
    </div>

    <!-- Command History -->
    <div v-if="commandHistory.length > 0" class="history-section">
      <div class="history-header">
        <i class="fa fa-history"></i>
        <span>Recent Commands</span>
        <button @click="clearHistory" class="clear-button">Clear</button>
      </div>
      <div class="history-list">
        <div
          v-for="(item, idx) in commandHistory.slice(-5).reverse()"
          :key="idx"
          @click="setCommand(item.command)"
          class="history-item"
          :class="{ 'error': item.error }"
        >
          <span class="history-command">{{ item.command }}</span>
          <span class="history-time">{{ formatTime(item.timestamp) }}</span>
          <span v-if="item.error" class="history-status">
            <i class="fa fa-exclamation-circle"></i>
          </span>
          <span v-else class="history-status">
            <i class="fa fa-check-circle"></i>
          </span>
        </div>
      </div>
    </div>

    <!-- Error Display -->
    <transition name="slide-up">
      <div v-if="error" class="error-message">
        <i class="fa fa-exclamation-triangle"></i>
        <span>{{ error }}</span>
        <button @click="error = null" class="dismiss-button">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </transition>

    <!-- Success Message -->
    <transition name="slide-up">
      <div v-if="successMessage" class="success-message">
        <i class="fa fa-check-circle"></i>
        <span>{{ successMessage }}</span>
      </div>
    </transition>

  </div>
</template>

<script>
import reportAssistantService from '@/services/reportAssistant.service';

export default {
  name: 'ReportAssistantDialog',
  props: {
    measure: {
      type: String,
      required: true
    },
    dimension: {
      type: String,
      default: null
    },
    currentFilters: {
      type: [Array, Object],
      default: () => []
    },
    currentSort: {
      type: Object,
      default: null
    },
    currentColumns: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      commandText: '',
      processing: false,
      error: null,
      successMessage: null,
      availableCommands: [],
      suggestions: [],
      commandHistory: reportAssistantService.loadCommandHistory(),
      historyIndex: -1,
      // Don't initialize with current state - only show what backend returns
      activeFilters: [],
      filterLogic: 'AND',
      currentSortState: null,
      activeColumns: [],
      pendingColumnOperation: 'add',  // Store the operation type for columns
      currentLimitState: null,
      currentGroupState: null,
      currentTimeRangeState: null,
      hasFilterChanges: false,
      hasSortChanges: false,
      hasColumnChanges: false,
      hasLimitChanges: false,
      hasGroupChanges: false,
      hasTimeRangeChanges: false,
      // Track whether sections should be shown (only after command execution)
      showFilterSection: false,
      showSortSection: false,
      showColumnsSection: false,
      showLimitSection: false,
      showGroupSection: false,
      showTimeRangeSection: false,
      
      // Natural language improvements
      placeholders: [
        "Try: show me sales over 1000",
        "Try: sort by revenue highest first", 
        "Try: hide the cost column",
        "Try: products with low stock",
        "Try: schedule this report weekly",
        "Try: top 10 customers by revenue",
        "Try: filter status equals shipped",
        "Try: show only active products"
      ],
      currentPlaceholderIndex: 0,
      placeholderInterval: null,
      autoApply: true,  // Auto-apply feature flag
      
      // Natural language suggestions
      selectedAction: null,
      actionExamples: {
        'filter': [
          'filter products where quantity < 10',
          'show only items with price > 100',
          'filter by status equals active',
          'exclude products with zero stock'
        ],
        'sort': [
          'sort by revenue highest first',
          'order by date newest to oldest',
          'sort products alphabetically',
          'arrange by quantity ascending'
        ],
        'time_range': [
          'last 30 days',
          'this month',
          'from Jan 1 to Jan 31',
          'year to date'
        ],
        'limit': [
          'show top 20 items',
          'limit to 50 results',
          'first 10 products',
          'bottom 5 performers'
        ],
        'group': [
          'group by product',
          'group by category',
          'switch to daily view',
          'change dimension to supplier'
        ],
        'columns': [
          'show columns quantity and revenue',
          'hide cost and margin columns',
          'add supplier column',
          'show only name and price'
        ]
      },
      commandTypes: [
        { id: 'filter', label: 'Filter', icon: 'fa fa-filter', color: '#2196F3' },
        { id: 'sort', label: 'Sort', icon: 'fa fa-sort', color: '#4CAF50' },
        { id: 'time_range', label: 'Time', icon: 'fa fa-calendar', color: '#00BCD4' },
        { id: 'limit', label: 'Limit', icon: 'fa fa-list-ol', color: '#FF9800' },
        { id: 'group', label: 'Group', icon: 'fa fa-object-group', color: '#795548' },
        { id: 'columns', label: 'Columns', icon: 'fa fa-columns', color: '#9C27B0' },
        
        // { id: 'schedule', label: 'Schedule', icon: 'fa fa-clock-o', color: '#607D8B' }
      ],
      commandExamples: {
        'filter': [
          'show products with sales over 1000',
          'only items where status is active',
          'products with stock less than 10',
          'filter by this month',
          'show items with price between 100 and 500'
        ],
        'sort': [
          'sort by revenue highest first',
          'arrange by date newest to oldest',
          'order by product name A to Z',
          'sort by quantity ascending'
        ],
        'columns': [
          'hide the cost column',
          'show only name, price and quantity',
          'add the supplier column',
          'remove all except essential fields'
        ],
        'limit': [
          'show top 10 items',
          'limit to 50 results',
          'show first 100 products',
          'display bottom 5 performers'
        ],
        'time_range': [
          'last 30 days',
          'this month',
          'yesterday',
          'year to date',
          'from Jan 1 to Jan 31',
          'last quarter',
          'compare to last year'
        ],
        'group': [
          'group by product',
          'group by variant',
          'group by location',
          'group by day',
        ],
        'schedule': [
          'email this report every Monday',
          'schedule daily at 9am',
          'send weekly summary on Fridays',
          'notify me monthly with this data'
        ]
      },
      
      // AI Processing Animation States
      processingMessages: [
        'Analyzing your request...',
        'Understanding the context...',
        'Preparing filters and sorts...',
        'Optimizing the query...',
        'Finalizing results...'
      ],
      currentProcessingMessage: 0,
      processingMessageInterval: null,
      processingButtonText: 'Processing...'
    };
  },
  computed: {
    hasActiveFilters() {
      return this.activeFilters && this.activeFilters.length > 0;
    },
    normalizedCommand() {
      return this.commandText.trim();
    },
    currentPlaceholder() {
      return this.placeholders[this.currentPlaceholderIndex];
    },
    currentActionExamples() {
      return this.selectedAction ? this.actionExamples[this.selectedAction] || [] : [];
    },
  },
  mounted() {
    this.loadAvailableCommands();
    this.focusInput();
    this.initializeFromProps();  // Still need to initialize to preserve existing filters
    this.startPlaceholderRotation();
  },
  
  beforeDestroy() {
    if (this.placeholderInterval) {
      clearInterval(this.placeholderInterval);
    }
    if (this.processingMessageInterval) {
      clearInterval(this.processingMessageInterval);
    }
  },
  methods: {
    initializeFromProps() {
      // Initialize activeFilters from currentFilters prop
      if (this.currentFilters) {
        if (Array.isArray(this.currentFilters)) {
          this.activeFilters = [...this.currentFilters];
        } else if (this.currentFilters.filters) {
          // Handle object format with filters array
          this.activeFilters = [...this.currentFilters.filters];
          this.filterLogic = this.currentFilters.logic || 'AND';
        }
      }
      
      // Initialize sort state from currentSort prop
      if (this.currentSort) {
        this.currentSortState = { ...this.currentSort };
      }
      
      // Initialize columns from currentColumns prop
      if (this.currentColumns && this.currentColumns.length > 0) {
        this.activeColumns = [...this.currentColumns];
      }
    },
    
    async loadAvailableCommands() {
      try {
        const response = await reportAssistantService.getAvailableCommands({
          measure: this.measure
        });
        this.availableCommands = response.commands || [];
        
        // Load merchant-specific examples as suggestions
        if (response.merchant_examples) {
          this.suggestions = response.merchant_examples.slice(0, 3);
        }
      } catch (error) {
        console.error('Failed to load commands:', error);
        // Service already provides default commands on error
      }
    },

    async executeCommand() {
      if (!this.commandText.trim() || this.processing) return;

      const command = this.normalizedCommand;
      this.processing = true;
      this.error = null;
      this.successMessage = null;
      
      // Start processing message rotation
      this.startProcessingMessages();
      
      // Don't clear existing filters/sort/columns
      // Only reset the change flags
      this.hasFilterChanges = false;
      this.hasSortChanges = false;
      this.hasColumnChanges = false;
      this.hasLimitChanges = false;
      this.hasGroupChanges = false;

      try {
        // Directly process the command - AI handles validation internally
        const response = await reportAssistantService.processCommand({
          command,
          measure: this.measure,
          dimension: this.dimension,
          current_state: {
            filters: this.activeFilters,
            sort: this.currentSortState,
            columns: this.activeColumns
          }
        });

        if (response.status === 'success') {
          // Apply the actions from backend response
          if (response.actions && response.actions.length > 0) {
            this.applyActions(response.actions);
          }
          
          this.addToHistory(command, false);
          this.successMessage = response.summary || 'Command executed successfully';
          this.commandText = '';
          
          // Auto-hide success message
          setTimeout(() => {
            this.successMessage = null;
          }, 3000);
        } else {
          throw new Error(response.error || 'Command execution failed');
        }
      } catch (error) {
        this.error = error.message || 'Failed to execute command';
        this.addToHistory(command, true);
      } finally {
        this.processing = false;
        this.stopProcessingMessages();
      }
    },

    applyActions(actions) {
      // Apply the new actions from AI command processing
      if (this.autoApply) {
        // For auto-apply: just update state, then apply all at once
        let hasAnyChanges = false;
        
        actions.forEach(action => {
          switch (action.type) {
            case 'filter':
              this.applyFilterAction(action.data);
              this.hasFilterChanges = true;
              hasAnyChanges = true;
              break;
            case 'sort':
              this.applySortAction(action.data);
              this.hasSortChanges = true;
              hasAnyChanges = true;
              break;
            case 'columns':
              this.applyColumnsAction(action.data);
              this.hasColumnChanges = true;
              hasAnyChanges = true;
              break;
            case 'limit':
              this.applyLimitAction(action.data);
              this.hasLimitChanges = true;
              hasAnyChanges = true;
              break;
            case 'group':
              this.applyGroupAction(action.data);
              this.hasGroupChanges = true;
              hasAnyChanges = true;
              break;
            case 'time_range':
              this.applyTimeRangeAction(action.data);
              this.hasTimeRangeChanges = true;
              hasAnyChanges = true;
              if (!this.autoApply) {
                this.showTimeRangeSection = true;
              }
              break;
          }
        });
        
        if (hasAnyChanges) {
          // Apply all changes immediately
          this.applyAllChanges();
        } else {
          // No changes detected, show message and close
          this.successMessage = 'No changes detected from command';
          this.$emit('close');
        }
      } else {
        // For manual apply: update state and show sections for review
        actions.forEach(action => {
          switch (action.type) {
            case 'filter':
              this.applyFilterAction(action.data);
              this.hasFilterChanges = true;
              this.showFilterSection = true;
              break;
            case 'sort':
              this.applySortAction(action.data);
              this.hasSortChanges = true;
              this.showSortSection = true;
              break;
            case 'columns':
              this.applyColumnsAction(action.data);
              this.hasColumnChanges = true;
              this.showColumnsSection = true;
              break;
            case 'limit':
              this.applyLimitAction(action.data);
              this.hasLimitChanges = true;
              this.showLimitSection = true;
              break;
            case 'group':
              this.applyGroupAction(action.data);
              this.hasGroupChanges = true;
              this.showGroupSection = true;
              break;
            case 'time_range':
              this.applyTimeRangeAction(action.data);
              this.hasTimeRangeChanges = true;
              this.showTimeRangeSection = true;
              break;
          }
        });
      }
    },

    clearAllSections() {
      // This method is kept for backwards compatibility but no longer used
      // We now preserve existing filters/sort/columns when executing new commands
      this.activeFilters = [];
      this.filterLogic = 'AND';
      this.currentSortState = null;
      this.activeColumns = [];
      this.currentLimitState = null;
      this.hasFilterChanges = false;
      this.hasSortChanges = false;
      this.hasColumnChanges = false;
      this.hasLimitChanges = false;
    },

    applyFilterAction(data) {
      // Handle different filter operations
      if (data.operation === 'clear') {
        // Clear all filters
        this.activeFilters = [];
        this.filterLogic = 'AND';
      } else if (data.operation === 'remove' && data.filterIndex !== undefined) {
        // Remove specific filter by index
        this.activeFilters.splice(data.filterIndex, 1);
      } else if (data.operation === 'replace') {
        // Replace all filters with new ones
        this.activeFilters = data.filters || [];
        if (data.logic) {
          this.filterLogic = data.logic;
        }
      } else if (data.filters) {
        // Default: Add or update filters
        data.filters.forEach(filter => {
          // Ensure filter has required properties
          const normalizedFilter = {
            code: filter.code || filter.column,
            name: filter.name || filter.columnName || filter.code,
            operator: filter.operator,
            value: filter.value,
            dataType: filter.dataType || 'string'
          };
          // Check if filter already exists with same column AND operator
          // This allows multiple filters on same column with different operators
          // (e.g., sales > 1000 AND sales < 3000)
          const existingIndex = this.activeFilters.findIndex(
            f => f.code === normalizedFilter.code && f.operator === normalizedFilter.operator
          );
          
          if (existingIndex >= 0) {
            // Replace existing filter with same column and operator
            this.$set(this.activeFilters, existingIndex, normalizedFilter);
          } else {
            // Add new filter
            this.activeFilters.push(normalizedFilter);
          }
        });
        // Update logic if provided
        if (data.logic) {
          this.filterLogic = data.logic;
        }
      }
    },

    applySortAction(data) {
      this.currentSortState = {
        column: data.column,
        columnName: data.columnName || data.column,
        direction: data.direction || 'desc'
      };
    },

    applyColumnsAction(data) {
      // Store the column action data for processing
      // Backend provides standardized operations: 'add', 'remove', 'only'
      this.pendingColumnOperation = data.operation || 'add';
      
      // Update local state with columns
      if (data.columns) {
        this.activeColumns = data.columns;
      }
    },

    applyLimitAction(data) {
      // Handle limit action
      this.currentLimitState = {
        limit: data.limit
      };
    },

    applyGroupAction(data) {
      // Handle group/dimension change action
      this.currentGroupState = {
        dimension: data.dimension,
        dimension_name: data.dimension_name,
        previous_dimension: data.previous_dimension
      };
    },

    applyTimeRangeAction(data) {
      // Handle time range action
      this.currentTimeRangeState = {
        time_range: data.time_range,
        description: data.description,
        has_comparison: data.has_comparison
      };
    },

    removeFilter(index) {
      this.activeFilters.splice(index, 1);
      // Set flag to show the Apply button
      this.hasFilterChanges = true;
    },

    clearFilters() {
      this.activeFilters = [];
      this.filterLogic = 'AND';
      this.$emit('filters-changed', [], 'AND');
    },

    clearSort() {
      this.currentSortState = null;
      // Set flag to show the Apply button
      this.hasSortChanges = true;
    },

    setCommand(command) {
      this.commandText = command;
      this.focusInput();
    },
    
    selectAction(type) {
      // Toggle action selection and insert keyword
      if (this.selectedAction === type) {
        // If clicking the same action, deselect it
        this.selectedAction = null;
      } else {
        // Select new action and insert its keyword
        this.selectedAction = type;
        const keyword = type.toLowerCase().replace('_', ' ');
        this.commandText = keyword + ' ';
        this.focusInput();
      }
    },
    
    getActionLabel(actionId) {
      const action = this.commandTypes.find(cmd => cmd.id === actionId);
      return action ? action.label : actionId;
    },

    onCommandInput() {
      // Generate suggestions based on input
      if (this.commandText.length > 2) {
        this.generateSuggestions();
      } else {
        this.suggestions = [];
      }
    },
    
    startPlaceholderRotation() {
      this.placeholderInterval = setInterval(() => {
        this.currentPlaceholderIndex = (this.currentPlaceholderIndex + 1) % this.placeholders.length;
      }, 4000);
    },

    generateSuggestions() {
      this.suggestions = reportAssistantService.generateSuggestions(this.commandText);
    },

    navigateHistory(direction) {
      if (this.commandHistory.length === 0) return;

      if (direction === -1) { // Up arrow
        if (this.historyIndex < this.commandHistory.length - 1) {
          this.historyIndex++;
          this.commandText = this.commandHistory[this.commandHistory.length - 1 - this.historyIndex].command;
        }
      } else { // Down arrow
        if (this.historyIndex > 0) {
          this.historyIndex--;
          this.commandText = this.commandHistory[this.commandHistory.length - 1 - this.historyIndex].command;
        } else {
          this.historyIndex = -1;
          this.commandText = '';
        }
      }
    },

    addToHistory(command, error = false) {
      this.commandHistory = reportAssistantService.addToCommandHistory(command, error);
      this.historyIndex = -1;
    },

    clearHistory() {
      reportAssistantService.clearCommandHistory();
      this.commandHistory = [];
    },

    focusInput() {
      this.$nextTick(() => {
        this.$refs.commandInput?.focus();
      });
    },

    getOperatorLabel(operator) {
      return reportAssistantService.formatOperator(operator);
    },

    formatFilterName(name) {
      // Format filter name to be more readable
      if (!name) return '';
      
      // Replace underscores with spaces and capitalize words
      return name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },

    formatValue(value) {
      return reportAssistantService.formatValue(value);
    },

    formatTime(timestamp) {
      return reportAssistantService.formatTimestamp(timestamp);
    },

    applyFilters() {
      // Emit ALL filter changes (existing + new)
      this.$emit('filters-changed', this.activeFilters, this.filterLogic);
      
      // Hide the filter section after applying
      this.showFilterSection = false;
      this.hasFilterChanges = false;
      
      // Keep the filters in memory but don't show the section
      // This preserves them for the next command execution
      
      // Show a success message
      if (!this.autoApply) {
        this.successMessage = 'Filters applied to report';
        setTimeout(() => {
          this.successMessage = null;
        }, 2000);
      }
    },

    applySort() {
      // Emit sort changes
      this.$emit('sort-changed', this.currentSortState);
      
      // Hide the sort section after applying
      this.showSortSection = false;
      this.hasSortChanges = false;
      
      // Keep the sort in memory but don't show the section
      // This preserves it for the next command execution
      
      // Show a success message
      if (!this.autoApply) {
        this.successMessage = 'Sort applied to report';
        setTimeout(() => {
          this.successMessage = null;
        }, 2000);
      }
    },

    applyLimit() {
      // Emit limit changes
      this.$emit('limit-changed', this.currentLimitState.limit);
      
      // Hide the limit section after applying
      this.showLimitSection = false;
      this.hasLimitChanges = false;
      
      // Show a success message
      if (!this.autoApply) {
        this.successMessage = `Limit set to ${this.currentLimitState.limit} items`;
        setTimeout(() => {
          this.successMessage = null;
        }, 2000);
      }
    },

    clearLimit() {
      this.currentLimitState = null;
      this.hasLimitChanges = true;
    },

    applyGroup() {
      // Emit dimension change event
      this.$emit('dimension-changed', this.currentGroupState.dimension);
      
      // Hide the group section after applying
      this.showGroupSection = false;
      this.hasGroupChanges = false;
      
      // Show a success message
      if (!this.autoApply) {
        this.successMessage = `Switching to ${this.currentGroupState.dimension_name}`;
        setTimeout(() => {
          this.successMessage = null;
        }, 2000);
      }
    },

    clearGroup() {
      this.currentGroupState = null;
      this.hasGroupChanges = true;
    },

    applyTimeRange() {
      // Emit time range changes
      this.$emit('time-range-changed', this.currentTimeRangeState.time_range);
      
      // Hide the time range section after applying
      this.showTimeRangeSection = false;
      this.hasTimeRangeChanges = false;
      
      // Show a success message
      if (!this.autoApply) {
        this.successMessage = `Time range set to ${this.currentTimeRangeState.description}`;
        setTimeout(() => {
          this.successMessage = null;
        }, 2000);
      }
    },

    clearTimeRange() {
      this.currentTimeRangeState = null;
      this.hasTimeRangeChanges = true;
    },

    formatTimeRange(timeRange) {
      // Format time range for display
      if (!timeRange) return '';
      
      // If there's a period, use it
      if (timeRange.period) {
        const periodMap = {
          'today': 'Today',
          'yesterday': 'Yesterday',
          'this-week': 'This week',
          'last-week': 'Last week',
          'this-month': 'This month',
          'last-month': 'Last month',
          'this-quarter': 'This quarter',
          'last-quarter': 'Last quarter',
          'this-year': 'This year',
          'last-year': 'Last year',
          'ytd': 'Year to date',
          'mtd': 'Month to date',
          'qtd': 'Quarter to date',
          'custom': 'Custom range'
        };
        
        // Check for days pattern
        if (timeRange.period.endsWith('d')) {
          const days = timeRange.period.slice(0, -1);
          return `Last ${days} days`;
        }
        
        return periodMap[timeRange.period] || timeRange.period;
      }
      
      // Fallback to from/to
      if (timeRange.from && timeRange.to) {
        return `${timeRange.from} to ${timeRange.to}`;
      }
      
      return 'Custom range';
    },

    applyAllChanges() {
      // Apply all pending changes
      let changesApplied = [];
      
      if (this.hasFilterChanges && this.activeFilters.length > 0) {
        this.applyFilters();
        changesApplied.push('filters');
      }
      if (this.hasSortChanges && this.currentSortState) {
        this.applySort();
        changesApplied.push('sort');
      }
      if (this.hasLimitChanges && this.currentLimitState) {
        this.applyLimit();
        changesApplied.push('limit');
      }
      if (this.hasGroupChanges && this.currentGroupState) {
        this.applyGroup();
        changesApplied.push('dimension');
      }
      if (this.hasTimeRangeChanges && this.currentTimeRangeState) {
        this.applyTimeRange();
        changesApplied.push('time range');
      }
      if (this.hasColumnChanges && this.activeColumns && this.activeColumns.length > 0) {
        // Apply columns changes
        this.applyColumns();
        changesApplied.push('columns');
      }
      
      if (this.autoApply && changesApplied.length > 0) {
        const message = `Auto-applied: ${changesApplied.join(', ')}`;
        this.successMessage = message;
        console.log('applyAllChanges', changesApplied);
        // Close dialog immediately after applying
        this.$emit('close');  // Emit close event to parent
      }
    },

    applyColumns() {
      // Emit column changes with the operation from the backend
      this.$emit('columns-changed', {
        operation: this.pendingColumnOperation,  // Use the operation from backend
        columns: this.activeColumns
      });
      
      // Hide the columns section after applying
      this.showColumnsSection = false;
      this.hasColumnChanges = false;
      
      // Show appropriate success message based on operation
      let message = '';
      if (this.pendingColumnOperation === 'remove') {
        message = `Removed ${this.activeColumns.length} column${this.activeColumns.length > 1 ? 's' : ''} from report`;
      } else if (this.pendingColumnOperation === 'only') {
        message = `Showing only ${this.activeColumns.length} column${this.activeColumns.length > 1 ? 's' : ''}`;
      } else {
        message = `Added ${this.activeColumns.length} column${this.activeColumns.length > 1 ? 's' : ''} to report`;
      }
      this.successMessage = message;
      
      // Clear the columns list and reset operation after applying
      this.activeColumns = [];
      this.pendingColumnOperation = 'add';  // Reset to default
      
      setTimeout(() => {
        this.successMessage = null;
      }, 2000);
    },

    clearColumns() {
      this.activeColumns = [];
      this.hasColumnChanges = true;
    },
    
    startProcessingMessages() {
      this.currentProcessingMessage = 0;
      this.processingButtonText = 'Thinking...';
      
      // Immediately show first message
      this.currentProcessingMessage = 0;
      
      // Rotate messages every 1.5 seconds
      this.processingMessageInterval = setInterval(() => {
        this.currentProcessingMessage = 
          (this.currentProcessingMessage + 1) % this.processingMessages.length;
        
        // Update button text periodically with variety
        const buttonTexts = ['Thinking...', 'Analyzing...', 'Processing...', 'Working on it...'];
        this.processingButtonText = 
          buttonTexts[Math.floor(Math.random() * buttonTexts.length)];
      }, 1500);
    },
    
    stopProcessingMessages() {
      if (this.processingMessageInterval) {
        clearInterval(this.processingMessageInterval);
        this.processingMessageInterval = null;
      }
      this.currentProcessingMessage = 0;
      this.processingButtonText = 'Processing...';
    }
  }
};
</script>

<style scoped lang="scss">
.report-assistant-dialog {
  background: white;
  padding: 20px;

  .assistant-subtitle {
    margin: 0 0 12px 0;
    font-size: 14px;
    color: #666;
    text-align: center;
  }
  
  .guidance-section {
    margin-bottom: 20px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    border-radius: 8px;
    border: 1px solid #bbdefb;
    
    .guidance-content {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 13px;
      color: #1565c0;
      
      i {
        font-size: 18px;
        color: #1976d2;
      }
      
      span {
        font-weight: 500;
      }
    }
  }

  .command-input-section {
    margin-bottom: 24px;

    .input-wrapper {
      display: flex;
      align-items: center;
      background: white;
      border: 2px solid #1976d2;
      border-radius: 8px;
      padding: 4px;
      transition: all 0.3s ease;
      position: relative;

      &:focus-within {
        border-color: #1565c0;
        background: white;
        box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
      }

      .command-input {
        flex: 1;
        padding: 12px 16px;
        border: none;
        background: transparent;
        font-size: 16px;
        color: #111827;
        outline: none;

        &::placeholder {
          color: #9ca3af;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }

      .execute-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 10px 20px;
        background: #757575;
        color: white;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(:disabled) {
          background: #616161;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        &:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        i {
          font-size: 14px;
        }
      }
    }

    .suggestions {
      margin-top: 8px;
      display: flex;
      gap: 8px;
      flex-wrap: wrap;

      .suggestion-item {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background: #fef3c7;
        border: 1px solid #fde68a;
        border-radius: 6px;
        font-size: 13px;
        color: #92400e;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #fde68a;
          transform: translateY(-1px);
        }

        i {
          font-size: 12px;
          color: #f59e0b;
        }
      }
    }
  }

  // Removed duplicate .action-section and orphaned styles - they are properly defined later in the file

  .history-section {
    background: #f9fafb;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;

    .history-header {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 12px;
      font-size: 14px;
      font-weight: 500;
      color: #6b7280;

      i {
        color: #9ca3af;
      }

      .clear-button {
        margin-left: auto;
        padding: 4px 8px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 4px;
        font-size: 12px;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #f3f4f6;
        }
      }
    }

    .history-list {
      display: flex;
      flex-direction: column;
      gap: 6px;

      .history-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        font-size: 13px;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #eef2ff;
          border-color: #c7d2fe;
        }

        &.error {
          background: #fef2f2;
          border-color: #fecaca;

          .history-status {
            color: #dc2626;
          }
        }

        .history-command {
          flex: 1;
          font-family: 'Monaco', 'Courier New', monospace;
          color: #374151;
        }

        .history-time {
          color: #9ca3af;
          font-size: 12px;
        }

        .history-status {
          color: #10b981;

          i {
            font-size: 14px;
          }
        }
      }
    }
  }

  .error-message,
  .success-message {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 14px;
    margin-top: 16px;

    i {
      font-size: 18px;
    }
  }

  .error-message {
    background: #fee2e2;
    border: 1px solid #fecaca;
    color: #991b1b;

    .dismiss-button {
      margin-left: auto;
      background: none;
      border: none;
      color: #dc2626;
      cursor: pointer;
      padding: 4px;

      &:hover {
        color: #991b1b;
      }
    }
  }

  .success-message {
    background: #d1fae5;
    border: 1px solid #a7f3d0;
    color: #065f46;
  }

  .apply-changes-section {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 2px solid #e5e7eb;
    display: flex;
    justify-content: center;

    .apply-changes-button {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 24px;
      background: #10b981;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 16px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      &:hover {
        background: #059669;
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      &:active {
        transform: translateY(0);
      }

      i {
        font-size: 18px;
      }
    }
  }

  .results-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    margin: 20px 0 12px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 8px;
    font-weight: 600;
    font-size: 14px;
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.2);

    i {
      font-size: 16px;
    }
  }

  .action-section {
    margin-bottom: 20px;
    padding: 15px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #f9f9f9;

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;

      .header-left {
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #666;
          font-size: 18px;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
    }

    .filter-list {
      margin: 10px 0;

      .filter-item {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        margin-bottom: 8px;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;

        .filter-name {
          font-weight: 500;
          color: #333;
          margin-right: 8px;
        }

        .filter-operator {
          color: #666;
          margin: 0 8px;
          padding: 2px 6px;
          background: #f0f0f0;
          border-radius: 3px;
          font-family: monospace;
          font-size: 13px;
        }

        .filter-value {
          color: #2196F3;
          font-weight: 500;
        }
        
        .remove-button {
          margin-left: auto;
          padding: 4px 8px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 4px;
          color: #757575;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 20px;
          font-weight: 400;
          line-height: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 28px;
          height: 28px;
          
          &:hover {
            background: #ffebee;
            color: #d32f2f;
            border-color: #ffcdd2;
          }
          
          &:active {
            background: #ffcdd2;
          }
          
          i {
            font-size: 14px;
            pointer-events: none;
          }
        }
      }

      .filter-logic {
        margin-top: 10px;
        padding: 8px;
        background: #fff3e0;
        border-radius: 4px;
        font-size: 13px;
        color: #e65100;
        display: inline-block;

        strong {
          font-weight: 600;
        }
      }
    }

    .sort-item,
    .limit-item,
    .group-item,
    .time-range-item {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      background: white;
      border: 1px solid #ddd;
      border-radius: 4px;

      span {
        font-weight: 500;
        color: #333;
        margin-right: 10px;
      }
      
      .date-range {
        font-size: 12px;
        color: #666;
        font-weight: 400;
        margin-left: 5px;
      }
      
      i.fa-arrow-up, i.fa-arrow-down {
        color: #2196F3;
        margin-right: auto;
      }
      
      .remove-button {
        margin-left: auto;
        padding: 4px 8px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        color: #757575;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 20px;
        font-weight: 400;
        line-height: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 28px;
        height: 28px;
        
        &:hover {
          background: #ffebee;
          color: #d32f2f;
          border-color: #ffcdd2;
        }
        
        &:active {
          background: #ffcdd2;
        }
      }

      .column-name {
        font-weight: 500;
        color: #333;
        margin-right: 10px;
      }

      .direction {
        display: inline-flex;
        align-items: center;
        padding: 4px 8px;
        background: #e3f2fd;
        border-radius: 3px;
        font-size: 12px;
        text-transform: uppercase;
        color: #1976d2;
        font-weight: 500;

        &.desc {
          background: #fce4ec;
          color: #c2185b;
        }

        i {
          margin-left: 4px;
          font-size: 14px;
        }
      }
    }

    .columns-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin: 10px 0;

      .column-chip,
      .column-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        background: white;
        border: 1px solid #ddd;
        border-radius: 16px;
        font-size: 14px;
        color: #333;
        transition: all 0.2s;

        &:hover {
          background: #f5f5f5;
          border-color: #999;
        }

        .fa {
          color: #666;
          font-size: 12px;
        }

        .column-name {
          font-weight: 500;
        }

        .column-type {
          color: #999;
          font-size: 12px;
          margin-left: 4px;
        }
      }
    }

    .action-buttons {
      display: flex;
      gap: 10px;
      margin-top: 15px;
      justify-content: flex-end;

      .apply-btn {
        padding: 10px 24px;
        background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        font-size: 14px;
        transition: all 0.3s;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        box-shadow: 0 2px 8px rgba(33, 150, 243, 0.25);
        text-transform: none;
        letter-spacing: 0.25px;

        &:hover {
          background: linear-gradient(135deg, #1976D2 0%, #1565C0 100%);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(33, 150, 243, 0.35);
        }

        &:active {
          transform: translateY(0);
        }

        &:disabled {
          background: #e0e0e0;
          color: #999;
          cursor: not-allowed;
          box-shadow: none;
        }

        i {
          font-size: 15px;
        }
      }

      .clear-btn {
        padding: 8px 16px;
        background: #f44336;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
        transition: background 0.3s;
        display: flex;
        align-items: center;
        gap: 6px;

        &:hover {
          background: #da190b;
        }

        i {
          font-size: 14px;
        }
      }
    }
  }
}

// Animations
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(10px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Natural Language Improvements */

.auto-apply-wrapper {
  margin-top: 12px;
  margin-bottom: 16px;
  
  .auto-apply-label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #495057;
    
    .auto-apply-checkbox {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }
    
    span {
      flex: 1;
      user-select: none;
    }
    
    .fa-info-circle {
      color: #6c757d;
      font-size: 14px;
      cursor: help;
    }
  }
}

.command-suggestions {
  margin-top: 20px;
  
  .keyword-header {
    margin-bottom: 12px;
    text-align: center;
    
    .keyword-title {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: #495057;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }
    
    .keyword-subtitle {
      display: block;
      font-size: 12px;
      color: #6c757d;
      font-style: italic;
    }
  }
  
  .command-chips {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 16px;
    justify-content: center;
    
    .command-chip.keyword-chip {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 14px;
      background: white;
      border: 1px dashed #d0d0d0;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s;
      font-size: 13px;
      font-weight: 400;
      position: relative;
      
      &:hover {
        border-style: solid;
        border-width: 2px;
        padding: 7px 13px;
        background: #fafafa;
      }
      
      &.active {
        border-style: solid;
        border-width: 2px;
        padding: 7px 13px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
      
      i:first-child {
        font-size: 14px;
      }
      
      .expand-icon {
        font-size: 10px;
        margin-left: 2px;
        color: #999;
      }
      
      span {
        color: #333;
      }
    }
  }
  
  .examples-container {
    background: #f9fafb;
    border-radius: 8px;
    padding: 12px;
    margin-top: 12px;
    
    &.compact {
      padding: 10px 12px;
      
      .examples-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        
        span {
          font-size: 12px;
          font-weight: 500;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }
        
        .close-examples {
          background: transparent;
          border: none;
          color: #9ca3af;
          cursor: pointer;
          padding: 2px 6px;
          font-size: 12px;
          transition: color 0.2s;
          
          &:hover {
            color: #6b7280;
          }
        }
      }
      
      .examples-grid {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        
        .example-chip {
          padding: 6px 12px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 12px;
          color: #4b5563;
          font-family: 'Monaco', 'Courier New', monospace;
          
          &:hover {
            background: #eef2ff;
            border-color: #c7d2fe;
            color: #1e40af;
          }
        }
      }
    }
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* AI Thinking Indicator Styles */
.ai-thinking-indicator {
  margin-top: 16px;
  padding: 20px;
  background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  animation: fadeIn 0.3s ease;
  
  .thinking-animation {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 12px;
    
    .thinking-dot {
      width: 12px;
      height: 12px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 50%;
      animation: thinking-pulse 1.4s ease-in-out infinite;
      
      &:nth-child(1) { animation-delay: 0s; }
      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
      &:nth-child(4) { animation-delay: 0.6s; }
    }
  }
  
  .thinking-message {
    text-align: center;
    color: #666;
    font-size: 14px;
    margin: 8px 0;
    font-style: italic;
    animation: fadeInOut 2s ease-in-out infinite;
  }
  
  .progress-bar {
    height: 4px;
    background: #e0e0e0;
    border-radius: 2px;
    overflow: hidden;
    margin-top: 12px;
    
    .progress-fill {
      height: 100%;
      background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
      border-radius: 2px;
      animation: progress-animation 3s ease-in-out infinite;
    }
  }
}

@keyframes thinking-pulse {
  0%, 80%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  40% {
    transform: scale(1.3);
    opacity: 0.7;
  }
}

@keyframes progress-animation {
  0% { width: 0; }
  50% { width: 70%; }
  100% { width: 95%; }
}

@keyframes fadeInOut {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Enhanced Button Processing State */
.execute-button.processing {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: shimmer 1.5s infinite;
  }
  
  .spinner-container {
    display: inline-block;
    margin-right: 8px;
    
    .ai-spinner {
      width: 14px;
      height: 14px;
      border: 2px solid rgba(255,255,255,0.3);
      border-top-color: white;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
      display: inline-block;
    }
  }
}

@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 100%; }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>